import { useEffect, useRef, useState } from "react";
import { Loader2, Search, Plus, Info } from "lucide-react";
import { toast } from "sonner";

import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { motorSearchJobcards } from "../lib/api";
import JobCardDetailModal from "./JobCardDetailModal";

function fmtPrecio(n) {
  if (n == null) return "";
  return new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n);
}

function tituloCorto(texto, max = 110) {
  if (!texto) return "";
  const primera = texto.split(".")[0]?.trim() ?? "";
  return primera.slice(0, max);
}

const CANTIDAD_DEFECTO_POR_UNIDAD = {
  ud: 1,
  uds: 1,
  m: 5,
  ml: 5,
  "m²": 10,
  m2: 10,
  "m³": 1,
  m3: 1,
  kg: 10,
  l: 5,
  h: 1,
};

function cantidadDefecto(unidad) {
  const u = (unidad ?? "").trim().toLowerCase();
  return CANTIDAD_DEFECTO_POR_UNIDAD[u] ?? 1;
}

export default function AddPartidaModal({
  open,
  onClose,
  onAdd,
  loading,
  initialQuery = "",
  contextoTitulo = null,
}) {
  const [query, setQuery] = useState("");
  const [resultados, setResultados] = useState([]);
  const [searching, setSearching] = useState(false);
  const [seleccionada, setSeleccionada] = useState(null);
  const [cantidad, setCantidad] = useState("");
  const [detalle, setDetalle] = useState(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setResultados([]);
      setSeleccionada(null);
      setCantidad("");
      setSearching(false);
    } else {
      setQuery(initialQuery ?? "");
      setSeleccionada(null);
      setCantidad("");
    }
  }, [open, initialQuery]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 3) {
      setResultados([]);
      return;
    }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await motorSearchJobcards({ query: q, topK: 8 });
        setResultados(data?.matches ?? []);
      } catch (e) {
        console.error(e);
        toast.error("Error buscando partidas");
        setResultados([]);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open]);

  const cantidadValida = (() => {
    const n = Number(cantidad);
    return Number.isFinite(n) && n > 0;
  })();
  const ready = !!seleccionada && cantidadValida;

  const handleAdd = () => {
    if (!ready) return;
    onAdd({
      jobcard: seleccionada,
      cantidad: Number(cantidad),
    });
  };

  const unidadSeleccionada = seleccionada?.unidad ?? "ud";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="add-partida-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-blue-600" />
            {contextoTitulo ? `Más opciones para "${contextoTitulo}"` : "Añadir partida"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
              {contextoTitulo
                ? "Refina la búsqueda para encontrar alternativas"
                : "Buscar partida (mínimo 3 letras)"}
            </Label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSeleccionada(null);
                }}
                placeholder="Ej: pintura plástica techo, pavimento parquet, alicatado cocina..."
                className="pl-9"
                autoFocus
                data-testid="add-partida-query"
              />
            </div>
          </div>

          <div className="grid gap-2 max-h-[320px] overflow-y-auto pr-1 min-h-[80px]">
            {searching && (
              <div className="flex items-center justify-center py-8 text-slate-500 gap-2 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Buscando…
              </div>
            )}
            {!searching && query.trim().length >= 3 && resultados.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">
                Sin resultados. Prueba otras palabras.
              </div>
            )}
            {!searching &&
              resultados.map((c) => {
                const selected = seleccionada?.id === c.id;
                return (
                  <div
                    key={c.id}
                    className={`relative rounded-md border transition-all ${
                      selected
                        ? "border-blue-600 bg-blue-50/60 ring-2 ring-blue-600"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setSeleccionada(c);
                        if (cantidad === "" || cantidad == null) {
                          setCantidad(cantidadDefecto(c.unidad));
                        }
                      }}
                      className="text-left w-full px-4 py-3 pr-12"
                      data-testid={`add-partida-opt-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="text-sm text-slate-800 leading-snug flex-1">
                          {tituloCorto(c.texto)}
                        </div>
                        {c.coste_total != null && (
                          <div className="text-sm font-semibold text-blue-700 tabular-nums whitespace-nowrap">
                            {fmtPrecio(c.coste_total)} €/{c.unidad ?? "ud"}
                          </div>
                        )}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetalle(c);
                      }}
                      title="Ver detalle"
                      className="absolute top-1/2 -translate-y-1/2 right-2 p-1.5 rounded-md text-slate-400 hover:text-blue-600 hover:bg-white/80"
                    >
                      <Info className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
          </div>

          {seleccionada && (
            <div className="max-w-xs">
              <Label className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2 block">
                Cantidad ({unidadSeleccionada})
              </Label>
              <Input
                type="number"
                min="0"
                step="any"
                value={cantidad ?? ""}
                onChange={(e) => setCantidad(e.target.value)}
                placeholder="Ej: 12"
                data-testid="add-partida-cantidad"
              />
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <Button variant="outline" onClick={onClose} className="border-slate-300">
              Cancelar
            </Button>
            <Button
              onClick={handleAdd}
              disabled={!ready || loading}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
              data-testid="add-partida-confirm-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Añadiendo…
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" /> Añadir
                </>
              )}
            </Button>
          </div>
        </div>

        <JobCardDetailModal
          open={!!detalle}
          onClose={() => setDetalle(null)}
          jobcard={detalle}
        />
      </DialogContent>
    </Dialog>
  );
}
